import React, { useCallback } from "react";
import { useForm, ArrayField } from "react-hook-form";
import { get } from "lodash";
import useTranslations from "../../hooks/useTranslations";
import { Input } from "../wombat-ui";

import { QuestionFormInput } from "./QuestionWithOptionsForm";
import SkillSearchPopover from "../skills/SkillSearchPopover";
import { SkillFragment } from '../../graphql/generated/definitions';

type QuestionFormTitleProps = {
  register: ReturnType<typeof useForm>["register"];
  unregister: ReturnType<typeof useForm>["unregister"];
  setValue: ReturnType<typeof useForm>["setValue"];
  getValues: ReturnType<typeof useForm>["getValues"];
  errors: ReturnType<typeof useForm>["errors"];
  error?: boolean;
  defaultValue: Partial<ArrayField<QuestionFormInput, "id">>;
  name: string;
};

const SKILL_ATTRIBUTES = ["id", "name", "kind", "locale", "__typename"];

const QuestionFormTitle: React.FC<QuestionFormTitleProps> = ({
  register,
  defaultValue,
  errors,
  setValue,
  getValues,
  name,
}) => {
  const t = useTranslations();

  const values = getValues();

  const handleItemSelected = useCallback(
    (item: SkillFragment) => {
      setValue(`${name}.label`, item.name);
      setValue(`${name}.skillId`, item.id);
      SKILL_ATTRIBUTES.forEach((attribute) =>
        setValue(`${name}.skill.${attribute}`, item[attribute])
      );
    },
    [setValue]
  );

  return (
    <>
      <div>
        <div className="grid w-full grid-cols-12 gap-x-4 gap-y-2">
          <div className="items-center w-full col-span-12">
            {defaultValue.type === "QuestionSkill" ? (
              <>
                <div>
                  <SkillSearchPopover
                    handleItemSelected={handleItemSelected}
                    baseId={`${name}.`}
                    defaultValue={
                      get(values, `${name}.label`) ||
                      defaultValue.skill?.name ||
                      ""
                    }
                  />
                </div>
                <input
                  type="hidden"
                  ref={register({ required: true })}
                  defaultValue={
                    get(values, `${name}.skillId`) ||
                    defaultValue.skillId ||
                    ""
                  } // really not sure why I have to do that
                  name={`${name}.skillId`}
                />
                <input
                  type="hidden"
                  ref={register()}
                  defaultValue={
                    get(values, `${name}.label`) ||
                    defaultValue.skill?.name ||
                    ""
                  }
                  name={`${name}.label`}
                />
                {SKILL_ATTRIBUTES.map((attribute) => (
                  <input
                    key={attribute}
                    type="hidden"
                    ref={register()}
                    defaultValue={
                      get(values, `${name}.skill.${attribute}`) ||
                      defaultValue.skill?.[attribute] ||
                      ""
                    }
                    name={`${name}.skill.${attribute}`}
                  />
                ))}
              </>
            ) : (
              <Input
                id={`${name}.label`}
                srOnlyLabel
                defaultValue={defaultValue.label}
                name={`${name}.label`}
                ref={register({
                  required: t("templates.titleRequired") as string,
                })}
                label={t("reviews.writeQuestionHere") as string}
                error={errors?.label}
                placeholder={t("reviews.writeQuestionHere") as string}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(QuestionFormTitle);
